import {
  getUseCaseInputDef,
  ENVELOPE_KEYS,
  type InputEnvelope,
  type InputFieldDef,
} from './useCaseInputRegistry';
import { isHiddenInputKey } from './plumbingKeys';

/**
 * Reduces a simulation's raw contentData to just what a human typed, chose or uploaded in the
 * use-case form, for the RLHF rating view.
 *
 * Registry fields come back first, in registry order, so the panel reads like the form. Anything
 * left over that isn't recognised plumbing comes back as `other` rather than being dropped —
 * a form gaining a field must never leave a rater judging output against a partial brief.
 *
 * That guarantee covers nested fields too: the search descends into every declared envelope, so
 * an unregistered key under `data` / `additional_data` / `user_product` still surfaces. Only the
 * envelope CONTAINERS are withheld, because their registered children are already listed above.
 */

export interface ResolvedInput {
  key: string;
  label: string;
  value: unknown;
}

export interface SelectedUserInputs {
  /** Known form fields, in the order the form presents them. */
  mapped: ResolvedInput[];
  /** Present in the payload, not plumbing, not in the registry. Shown collapsed. */
  other: ResolvedInput[];
  /** Registry label for the use case, or null when the task is unmapped. */
  useCaseLabel: string | null;
}

type ContentData = Record<string, unknown>;

/**
 * Empty is not the same as absent, but for a rater both mean "the author gave nothing here", and
 * a column of blank rows buries the fields that do carry a brief. Booleans are exempt: `false` is
 * a real answer to a toggle.
 */
const hasContent = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
};

const readEnvelope = (contentData: ContentData, from: InputEnvelope): ContentData => {
  if (from === 'root') return contentData;
  const nested = contentData[ENVELOPE_KEYS[from]];
  return nested && typeof nested === 'object' && !Array.isArray(nested)
    ? (nested as ContentData)
    : {};
};

const resolveField = (contentData: ContentData, field: InputFieldDef): ResolvedInput | null => {
  const source = readEnvelope(contentData, field.from ?? 'root');
  const value = source[field.key];
  return hasContent(value) ? { key: field.key, label: field.label, value } : null;
};

/** `content_subject` -> `Content subject`. Only used for keys with no registry label. */
export const humanizeKey = (key: string): string => {
  const spaced = key.replace(/[_-]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * A base64/data-URI payload is an image, not part of the brief. Dedicated image blocks render
 * the ones a rater needs; listing the raw URI as "other input" would paste megabytes of text.
 */
const isMediaPayload = (value: unknown): boolean =>
  typeof value === 'string' && value.startsWith('data:');

const leftoversIn = (source: ContentData, claimedKeys: ReadonlySet<string>): ResolvedInput[] =>
  Object.entries(source)
    .filter(
      ([key, value]) =>
        !claimedKeys.has(key) &&
        !isHiddenInputKey(key) &&
        !isMediaPayload(value) &&
        hasContent(value),
    )
    .map(([key, value]) => ({ key, label: humanizeKey(key), value }));

export const selectUserInputs = (
  task: unknown,
  contentData: ContentData | null | undefined,
): SelectedUserInputs => {
  if (!contentData) return { mapped: [], other: [], useCaseLabel: null };

  const def = getUseCaseInputDef(task);
  const fields = def?.fields ?? [];

  const mapped = fields
    .map((field) => resolveField(contentData, field))
    .filter((entry): entry is ResolvedInput => entry !== null);

  // Claimed keys are tracked PER ENVELOPE. A nested key (e.g. data.brand) must not suppress a
  // same-named root key, which is a real shape in the packaging payloads.
  const claimedIn = (envelope: InputEnvelope): ReadonlySet<string> =>
    new Set(fields.filter((f) => (f.from ?? 'root') === envelope).map((f) => f.key));

  // The safety net descends INTO the envelopes. The container itself stays hidden (it is in
  // RICH_RENDER_KEYS, so listing it whole would restate its own extracted fields as raw JSON),
  // but its unregistered children surface individually — otherwise an authored field nested
  // under `data` / `additional_data` / `user_product` would be invisible in both lanes, which
  // is exactly the partial-brief failure this lane exists to prevent.
  const nestedLeftovers = (Object.keys(ENVELOPE_KEYS) as Exclude<InputEnvelope, 'root'>[]).flatMap(
    (envelope) => leftoversIn(readEnvelope(contentData, envelope), claimedIn(envelope)),
  );

  return {
    mapped,
    other: [...leftoversIn(contentData, claimedIn('root')), ...nestedLeftovers],
    useCaseLabel: def?.label ?? null,
  };
};
